import { useCallback, useEffect, useRef, useState } from 'react';
import { toastError } from '@components/common/Toast/CustomToast';
import { api } from '@/api/growth/map/api';

interface GrowthDashboardMapProps {
    lat?: string;
    lon?: string;
    cropName?: string;
}

const GrowthDashboardMap = ({
    lat,
    lon,
    cropName,
}: GrowthDashboardMapProps) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<any>(null);
    const [cadastralPolygons, setCadastralPolygons] = useState<any[]>([]);
    const [infoWindow, setInfoWindow] = useState<any>(null);
    const cropMarkerRef = useRef<any>(null);

    const CROP_IMAGE_MAP = {
        옥수수: {
            path: '/images/crop/corn.png',
        },
        감자: {
            path: '/images/crop/potato.png',
        },
        벼: {
            path: '/images/crop/rice.png',
        },
        콩: {
            path: '/images/crop/soybean.png',
        },
        잔디: {
            path: '/images/crop/bermudagrass.png',
        },
    } as const;

    useEffect(() => {
        const loadNaverMapScript = () => {
            if (window.naver && window.naver.maps) {
                initializeMap();
                return;
            }

            const existingScript = document.querySelector(
                'script[src*="openapi.map.naver.com"]',
            );
            if (existingScript) {
                existingScript.remove();
            }

            const clientId = import.meta.env.VITE_NCP_API_KEY_ID;
            const script = document.createElement('script');
            script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}&submodules=geocoder,drawing,geometry`;
            script.type = 'text/javascript';
            script.async = true;

            script.onload = () => {
                console.log('NCP 네이버 지도 API 로드 완료');
                setTimeout(initializeMap, 200);
            };

            script.onerror = error => {
                console.error('NCP 네이버 지도 API 로드 실패:', error);
            };

            document.head.appendChild(script);
        };

        loadNaverMapScript();

        return () => {
            cadastralPolygons.forEach(polygon => {
                if (polygon) {
                    polygon.setMap(null);
                }
            });

            if (cropMarkerRef.current) {
                cropMarkerRef.current.setMap(null);
                cropMarkerRef.current = null;
            }

            if (infoWindow) {
                infoWindow.close();
            }
        };
    }, []);

    useEffect(() => {
        if (map && lat && lon) {
            const latitude = parseFloat(lat);
            const longitude = parseFloat(lon);

            if (!isNaN(latitude) && !isNaN(longitude)) {
                const latlng = new window.naver.maps.LatLng(
                    latitude,
                    longitude,
                );

                map.panTo(latlng, {
                    duration: 500,
                    easing: 'easeOutCubic',
                });

                map.setZoom(17);

                fetchMapFeature(map, longitude, latitude);
            }
        }
    }, [map, lat, lon]);

    const initializeMap = useCallback(() => {
        if (!mapRef.current || !window.naver || !window.naver.maps) {
            console.warn('지도 초기화 조건이 충족되지 않았습니다.');
            return;
        }

        try {
            const mapOptions = {
                center: new window.naver.maps.LatLng(37.5666805, 126.9784147),
                zoom: 17,
                minZoom: 6,
                maxZoom: 21,
                mapTypeId: window.naver.maps.MapTypeId.NORMAL,
                mapDataControl: false,
                scaleControl: true,
                logoControl: true,
                mapTypeControl: true,
                mapTypeControlOptions: {
                    style: window.naver.maps.MapTypeControlStyle.BUTTON,
                    position: window.naver.maps.Position.TOP_RIGHT,
                },
                zoomControl: true,
                zoomControlOptions: {
                    style: window.naver.maps.ZoomControlStyle.LARGE,
                    position: window.naver.maps.Position.TOP_LEFT,
                },
            };

            const naverMap = new window.naver.maps.Map(
                mapRef.current,
                mapOptions,
            );

            const cadastral = new window.naver.maps.CadastralLayer();
            cadastral.setMap(naverMap);

            setMap(naverMap);

            console.log('NCP 네이버 지도가 성공적으로 초기화되었습니다.');
        } catch (error) {
            console.error('지도 초기화 중 오류:', error);
            toastError(
                '지도 초기화에 실패했습니다. 페이지를 새로고침해주세요.',
            );
        }
    }, []);

    const fetchMapFeature = useCallback(
        async (currentMap: any, lng: number, lat: number) => {
            try {
                const featureData = await api.getMapFeature(
                    {
                        longitude: lng,
                        latitude: lat,
                    },
                    { skipLoading: true },
                );

                if (!featureData || !featureData.coordinates) {
                    return;
                }

                cadastralPolygons.forEach(polygon => {
                    if (polygon) {
                        polygon.setMap(null);
                    }
                });
                setCadastralPolygons([]);

                const coordinates = featureData.coordinates;
                const polygonPaths: any[] = [];

                coordinates.forEach((polygon: number[][][]) => {
                    const exteriorRing = polygon[0];
                    const path = exteriorRing.map(
                        (coord: number[]) =>
                            new window.naver.maps.LatLng(coord[1], coord[0]),
                    );
                    polygonPaths.push(path);
                });

                const cadastralPolygon = new window.naver.maps.Polygon({
                    map: currentMap,
                    paths: polygonPaths,
                    fillColor: '#03fd50',
                    fillOpacity: 0.5,
                    strokeColor: '#007a0e',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    strokeStyle: 'solid',
                    strokeLineCap: 'round',
                    clickable: true,
                    zIndex: 2,
                });

                setCadastralPolygons([cadastralPolygon]);

                const center = getCenter(cadastralPolygon);
                currentMap.panTo(center, {
                    duration: 500,
                    easing: 'easeOutCubic',
                });

                // 폴리곤 클릭 이벤트
                window.naver.maps.Event.addListener(
                    cadastralPolygon,
                    'click',
                    () => {
                        addInfoWindow(currentMap, featureData, center);
                    },
                );

                // 호버 이벤트
                window.naver.maps.Event.addListener(
                    cadastralPolygon,
                    'mouseover',
                    function () {
                        cadastralPolygon.setOptions({
                            fillOpacity: 0.7,
                        });
                    },
                );

                window.naver.maps.Event.addListener(
                    cadastralPolygon,
                    'mouseout',
                    function () {
                        cadastralPolygon.setOptions({
                            fillOpacity: 0.5,
                        });
                    },
                );

                // 작물 마커 추가
                if (cropName) {
                    addCropMarker(currentMap, center);
                }
            } catch (error) {
                console.error('지적도 정보 조회 중 오류:', error);
            }
        },
        [cadastralPolygons, cropName, infoWindow],
    );

    const getCenter = (polygon: any): any => {
        const bounds = new window.naver.maps.LatLngBounds();
        const paths = polygon.getPaths();

        paths.forEach((path: any) => {
            for (let i = 0; i < path.getLength(); i++) {
                bounds.extend(path.getAt(i));
            }
        });

        const centerLat = (bounds.getNE().lat() + bounds.getSW().lat()) / 2;
        const centerLng = (bounds.getNE().lng() + bounds.getSW().lng()) / 2;
        return new window.naver.maps.LatLng(centerLat, centerLng);
    };

    const formatPyeong = (areaInSquareMeters: number): number => {
        if (!areaInSquareMeters || isNaN(areaInSquareMeters)) return 0;
        const result = parseFloat(String(areaInSquareMeters)) / 3.3058;
        return Number(result.toFixed(2));
    };

    const addInfoWindow = (currentMap: any, featureData: any, latLng: any) => {
        // 기존 정보창 닫기
        if (infoWindow) {
            infoWindow.close();
        }

        const jimok = featureData.properties.jimok;
        const area = featureData.properties.area;
        const pyeong = formatPyeong(area);

        const infoContent = `
            <div class="custom-infowindow">
                <button
                    onclick="closeInfoWindow()"
                    style="
                        position: absolute;
                        top: 13px;
                        right: 11px;
                        width: 20px;
                        height: 20px;
                        border: none;
                        background: transparent;
                        color: #666;
                        font-size: 14px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        line-height: 1;
                        z-index: 1000;
                    "
                    title="닫기"
                >
                    <i class="ri-close-large-line"></i>
                </button>
                <div class="text-primary fw-bold border-bottom mb-2 pb-2">
                    <i class="ri-map-pin-line"></i> 농지 정보
                </div>
                <div class="row-item">
                    <strong>주소:</strong> ${featureData.properties?.roadAddr || featureData.properties?.addr}
                </div>
                <div class="row-item">
                    <strong>지번:</strong> ${featureData.properties?.jibun || '정보 없음'}
                </div>
                ${
                    area
                        ? `
                    <div class="row-item">
                        <strong>면적:</strong> ${area.toLocaleString()}㎡ / ${pyeong?.toLocaleString()}평
                    </div>
                `
                        : ''
                }
                <div class="row-item">
                    <strong>지목:</strong> ${jimok || '정보 없음'}
                </div>
                ${
                    cropName
                        ? `
                    <div class="row-item">
                        <strong>작물:</strong> ${cropName}
                    </div>
                `
                        : ''
                }
            </div>
        `;

        const newInfoWindow = new window.naver.maps.InfoWindow({
            content: infoContent,
            maxWidth: 400,
            borderColor: '#a8a8a8',
            borderWidth: 0,
            backgroundColor: 'transparent',
            disableAnchor: true,
            pixelOffset: new window.naver.maps.Point(0, -50),
        });

        newInfoWindow.open(currentMap, latLng);
        setInfoWindow(newInfoWindow);

        (window as any).closeInfoWindow = () => {
            if (newInfoWindow && newInfoWindow.getMap()) {
                newInfoWindow.close();
            }
        };
    };

    const addCropMarker = (currentMap: any, center: any) => {
        // 기존 마커 제거
        if (cropMarkerRef.current) {
            cropMarkerRef.current.setMap(null);
            cropMarkerRef.current = null;
        }

        // 작물 이름이 없으면 마커 생성 안 함
        if (!cropName) {
            return;
        }

        // 작물 이미지 선택
        const cropInfo =
            CROP_IMAGE_MAP[cropName as keyof typeof CROP_IMAGE_MAP];

        // 해당 작물 이미지가 없으면 마커 생성 안 함
        if (!cropInfo) {
            return;
        }

        const newMarker = new window.naver.maps.Marker({
            position: center,
            map: currentMap,
            title: cropName,
            icon: {
                content: `
                    <div class="custom-marker bounce">
                        <img src="${cropInfo.path}" width="30" height="30" />
                    </div>
                `,
                scaledSize: new window.naver.maps.Size(30, 30),
                origin: new window.naver.maps.Point(0, 0),
                anchor: new window.naver.maps.Point(15, 30),
            },
            clickable: false,
            zIndex: 100,
        });

        cropMarkerRef.current = newMarker;
    };

    return (
        <div className="card custom-card">
            <div className="card-body">
                <div ref={mapRef} style={{ minHeight: '320px' }} />
            </div>
        </div>
    );
};

export default GrowthDashboardMap;
